import React, { useContext, useMemo, useState } from 'react';
import { CompanyRoleAssignmentContext } from '../../../../../../../context/CompanyRoleAssignmentContext';
import { StockLocationContext } from '../../../../../../../context/StockLocationContext';
import { StockLocationRoleAssignmentContext } from '../../../../../../../context/StockLocationRoleAssignmentContext';
import { TagRelationContext } from '../../../../../../../context/TagRelationContext';
import { UserContext } from '../../../../../../../context/UserContext';
import {
  StockLocationMutations,
  UpdateStockLocationResponse,
  UpdateStockLocationVariables,
} from '../../../../../../../graphql/stockLocation.graphql';
import {
  UpdateStockLocationRoleAssignmentResponse,
  UpdateStockLocationRoleAssignmentVariables,
  StockLocationRoleAssignmentMutation,
} from '../../../../../../../graphql/stockLocationRoleAssignment.graphql';
import useMutation from '../../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../../providers/ToastProvider';
import { CompanyRole } from '../../../../../../../types/company_role_assignment';
import { StockLocationInput } from '../../../../../../../types/stock_location';
import { StockLocation } from '../../../../../../../types/stock_location/stockLocation';
import {
  StockLocationRoleAssignment,
  StockLocationRole,
  StockLocationRoleAssignmentUtil,
  StockLocationRoleAssignmentInput,
} from '../../../../../../../types/stock_location_role_assignment';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../../types/translation/Translator';
import { StockLocationRoleAssignmentFilter } from '../../../../../../../VentoryUI/components/filters/StockLocationRoleAssignmentFilter/StockLocationRoleAssignmentFilter';
import { AddButton } from '../../../../../../components/Button';
import { Checkbox } from '../../../../../../components/Checkbox';
import { TableDropdown } from '../../../../../../components/Dropdown';
import { SearchFilter } from '../../../../../../components/Filter';
import Table from '../../../../../../components/Table';
import { ToastType } from '../../../../../../components/Toast';
import { DeleteIconButton } from '../../../../../../icons';
import CreateStockLocationRoleForStockLocationModal from './CreateStockLocationRoleForStockLocationModal';
import DeleteStockLocationRoleForStockLocationModal from './DeleteStockLocationRoleForStockLocationModal';
import { CompanyContext } from '../../../../../../../context/CompanyContext';

interface StockLocationUsersPaneProps {
  stockLocation: StockLocation;
}

export default function StockLocationUsersPane({ stockLocation }: StockLocationUsersPaneProps) {
  const { showToast } = useToast();

  const { tagRelations } = useContext(TagRelationContext);
  const { companyUsers } = useContext(UserContext);
  const { stockLocationRoles, setStockLocationRoles } = useContext(StockLocationRoleAssignmentContext);
  const { companyRoles } = useContext(CompanyRoleAssignmentContext);
  const { stockLocations, setStockLocations } = useContext(StockLocationContext);
  const { currentCompany } = useContext(CompanyContext);

  const companyAdmins = useMemo(() => {
    return [...companyRoles.values()]
      .flat()
      .filter(cr => cr.companyId === stockLocation.companyId && cr.role === CompanyRole.administrator);
  }, [companyRoles]);

  const allItems = useMemo(() => {
    const rolesForStockLocation = stockLocationRoles.get(stockLocation.id) || [];

    return [
      ...rolesForStockLocation,
      ...companyAdmins.map(
        ca =>
          new StockLocationRoleAssignment({
            companyId: stockLocation.companyId,
            stockLocationId: stockLocation.id,
            role: StockLocationRole.STOCK_LOCATION_MANAGER,
            userId: ca.userId,
          }),
      ),
    ];
  }, [stockLocation, stockLocationRoles]);

  const [items, setItems] = useState(allItems);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteRole, setDeleteRole] = useState<StockLocationRoleAssignment | null>(null);

  const [updateStockLocation, { loading: updateStockLocationLoading }] = useMutation<
    UpdateStockLocationResponse,
    UpdateStockLocationVariables
  >(StockLocationMutations.update, {
    onCompleted: response => {
      const updatedStockLocation = response.updateStockLocation[0];
      setStockLocations(new Map(stockLocations).set(updatedStockLocation.id, updatedStockLocation));
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationUpdatedSuccessfullyToast).singular.upper,
      });
    },
  });

  const [updateRoleAssignment, { loading }] = useMutation<
    UpdateStockLocationRoleAssignmentResponse,
    UpdateStockLocationRoleAssignmentVariables
  >(StockLocationRoleAssignmentMutation.update, {
    onCompleted: response => {
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationRoleUpdatedSuccessfullyToastTitle).singular.upper,
      });
      const updatedStockLocationRole = new StockLocationRoleAssignment(response.updateStockLocationRoleAssignment[0]);
      const existing = stockLocationRoles.get(updatedStockLocationRole.stockLocationId) || [];
      stockLocationRoles.set(
        updatedStockLocationRole.stockLocationId,
        existing.map(slr => (slr.id === updatedStockLocationRole.id ? updatedStockLocationRole : slr)),
      );
      setStockLocationRoles(new Map(stockLocationRoles));
    },
  });

  const filter = new StockLocationRoleAssignmentFilter(
    tagRelations,
    new Map([[stockLocation.id, stockLocation]]),
    currentCompany,
  );

  return (
    <>
      {addModalOpen && (
        <CreateStockLocationRoleForStockLocationModal
          open={addModalOpen}
          setOpen={setAddModalOpen}
          stockLocation={stockLocation}
        />
      )}
      {deleteRole && (
        <DeleteStockLocationRoleForStockLocationModal
          assignment={deleteRole}
          setOpen={open => !open && setDeleteRole(null)}
        />
      )}

      <div className='flex flex-grow flex-col gap-4'>
        <div className='flex justify-between'>
          <SearchFilter filter={filter} items={allItems} setItems={setItems} />
          <AddButton text={t(TranslationKey.addUserToLocation).singular.upper} onClick={() => setAddModalOpen(true)} />
        </div>
        <Table
          items={items}
          filter={filter}
          headers={[
            {
              key: 'name',
              name: t(TranslationKey.name).singular.upper,
              text: item => companyUsers.get(item.userId)?.fullName || t(TranslationKey.unknownUser).singular.upper,
            },
            {
              key: 'email',
              name: t(TranslationKey.email).singular.upper,
              text: item => companyUsers.get(item.userId)?.email || t(TranslationKey.unknownUser).singular.upper,
            },
            {
              key: 'role',
              name: t(TranslationKey.role).singular.upper,
              position: 'start',
              text: item => {
                if (companyAdmins.find(ca => ca.userId === item.userId)) return 'Manager (Administrator)';

                return (
                  <div className='w-full py-2'>
                    <TableDropdown
                      items={Object.values(StockLocationRole)}
                      value={item.role}
                      onSelect={role => {
                        updateRoleAssignment({
                          variables: {
                            stockLocationRoleAssignments: [
                              new StockLocationRoleAssignmentInput(item).withRole(role).forUpdate(),
                            ],
                          },
                        });
                      }}
                      textBuilder={StockLocationRoleAssignmentUtil.roleToString}
                    />
                  </div>
                );
              },
            },
            {
              key: 'primary_contact',
              name: t(TranslationKey.primaryContact).singular.upper,
              text: item => (
                <Checkbox
                  value={stockLocation.administrators.includes(item.userId)}
                  onChange={value => {
                    const entity = new StockLocationInput(stockLocation);
                    if (value) entity.withAdministrator(item.userId);
                    else entity.withoutAdministrator(item.userId);
                    updateStockLocation({ variables: { stockLocations: [entity.forUpdate()] } });
                  }}
                />
              ),
            },
            {
              key: 'delete',
              name: '',
              text: item => {
                if (!item.id) return null;
                return <DeleteIconButton onClick={() => setDeleteRole(item)} />;
              },
            },
          ]}
        />
      </div>
    </>
  );
}
